import React from 'react';
import { createPortal } from 'react-dom';
import { Plus, Search, X } from 'lucide-react';
import { US_STATES, US_STATE_MAP } from '../../../data/usStates';
import { computeAllowedStates } from '../../../data/stateAdjacency';
import InteractiveStatesMap from './InteractiveStatesMap';

interface StateSelectorProps {
  selectedStates: string[];
  onChange: (next: string[]) => void;
  accentColor: string;
  // 2-letter state code derived from the geocoded business address (the
  // tabs' AddressLeadIn does the point-in-polygon). When provided and not
  // already in `selectedStates`, surface a one-click "Based on your
  // address" suggestion chip. We avoid auto-selecting so a user who only
  // wants the address-driven radius doesn't trip the conflict modal.
  addressSuggestedStateCode?: string | null;
  // 2-letter state codes the publisher declared at onboarding (their
  // coveredStates list). Rendered as a row of one-click "Suggested states"
  // chips below the map. Same opt-in model as the DMA suggestions — the
  // chip seeds the selection on click; nothing auto-applies.
  suggestedStateCodes?: string[];
  // Show the deck.gl InteractiveStatesMap above the search. Defaults to
  // true (wizard usage); the staff onboarding form passes false so each
  // market row doesn't render a 420px map.
  showMap?: boolean;
}

// Multi-select of US states + DC, returning state codes ("CA", "NY", …).
// Two modes:
//   - showMap=true (wizard) → search input above the map; the deck.gl
//     map is still the primary visual picker, but typing in the search
//     lets a buyer who knows the state by name pin it without hunting
//     for the polygon. Address-derived + onboarded chips also surface.
//   - showMap=false (staff PublisherForm market rows) → search input +
//     portaled dropdown is the only picker. A map per market row would
//     be too heavy, and the staff form has no chip suggestions to seed.
const StateSelector: React.FC<StateSelectorProps> = ({
  selectedStates,
  onChange,
  accentColor,
  addressSuggestedStateCode,
  suggestedStateCodes,
  showMap = true,
}) => {
  // Allowed set = the publisher's onboarded coverage ∪ states bordering
  // any of them. null = no restriction (legacy stations without
  // coveredStates declared get the full national map).
  const allowedStates = React.useMemo(
    () => computeAllowedStates(suggestedStateCodes ?? []),
    [suggestedStateCodes],
  );
  const allowedSet = React.useMemo(
    () => (allowedStates ? new Set(allowedStates) : null),
    [allowedStates],
  );

  // Search + portaled-dropdown plumbing — only rendered when showMap=false.
  // Hooks must declare unconditionally (Rules of Hooks); rendering is gated.
  const [search, setSearch] = React.useState('');
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const [anchorRect, setAnchorRect] = React.useState<{ top: number; left: number; width: number } | null>(null);

  const measureAnchor = React.useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setAnchorRect({ top: r.bottom + 4, left: r.left, width: r.width });
  }, []);

  React.useEffect(() => {
    if (!dropdownOpen) return;
    measureAnchor();
    const handleClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (anchorRef.current?.contains(t)) return;
      if (dropdownRef.current?.contains(t)) return;
      setDropdownOpen(false);
    };
    const handleScrollOrResize = () => measureAnchor();
    document.addEventListener('mousedown', handleClick);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [dropdownOpen, measureAnchor]);

  const filtered = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    const pool = allowedSet
      ? US_STATES.filter((s) => allowedSet.has(s.code))
      : US_STATES;
    if (!term) return pool;
    return pool.filter(
      (s) => s.name.toLowerCase().includes(term) || s.code.toLowerCase().includes(term),
    );
  }, [search, allowedSet]);

  const add = (code: string) => {
    if (selectedStates.includes(code)) return;
    onChange([...selectedStates, code]);
    // Dismiss the dropdown so the buyer sees their selection register on
    // the map / chip row. Re-focusing the input re-opens it for adding the
    // next state.
    setSearch('');
    setDropdownOpen(false);
  };

  const remove = (code: string) => {
    onChange(selectedStates.filter((s) => s !== code));
  };

  const showAddressSuggestion =
    !!addressSuggestedStateCode && !selectedStates.includes(addressSuggestedStateCode);

  // Filter onboarded codes to ones still unpicked AND known to US_STATE_MAP
  // (drops typos like "ZZ" that staff might have entered in PublisherForm).
  const onboardingSuggestions = React.useMemo(() => {
    if (!suggestedStateCodes || suggestedStateCodes.length === 0) return [];
    return suggestedStateCodes.filter(
      (code) => !!US_STATE_MAP[code] && !selectedStates.includes(code),
    );
  }, [suggestedStateCodes, selectedStates]);

  return (
    <div className="space-y-3">
      <div className="relative" ref={anchorRef}>
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          aria-label="Search states"
          placeholder="Search states by name…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setDropdownOpen(true);
          }}
          onFocus={() => setDropdownOpen(true)}
          className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-1"
          style={{ ['--tw-ring-color' as string]: accentColor }}
        />
      </div>
      {dropdownOpen && anchorRect && createPortal(
        <div
          ref={dropdownRef}
          // Portal escapes any ancestor `overflow: hidden` (e.g., the
          // PublisherForm Section card or the GeoTargetingTabs measured-
          // height wrapper). Position is fixed-from-viewport and recomputed
          // in the open/scroll/resize effect above.
          style={{
            position: 'fixed',
            top: anchorRect.top,
            left: anchorRect.left,
            width: anchorRect.width,
            zIndex: 50,
          }}
          className="bg-white border border-slate-200 rounded-lg shadow-lg max-h-96 overflow-y-auto"
        >
          {filtered.length > 0 ? (
            filtered.map((state) => {
              const isSelected = selectedStates.includes(state.code);
              return (
                <button
                  key={state.code}
                  type="button"
                  onClick={() => add(state.code)}
                  disabled={isSelected}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 ${
                    isSelected ? 'text-slate-400 cursor-not-allowed' : 'text-slate-900'
                  }`}
                >
                  <span className="font-medium">{state.code}</span>
                  <span className="text-slate-500"> — {state.name}</span>
                </button>
              );
            })
          ) : (
            <div className="px-3 py-2 text-sm text-slate-500">No states found</div>
          )}
        </div>,
        document.body,
      )}
      {showMap && (
        <>
          <InteractiveStatesMap
            selectedStates={selectedStates}
            onChange={onChange}
            accentColor={accentColor}
            allowedStates={allowedStates}
            focusStates={suggestedStateCodes}
          />
          {allowedStates && (
            <p className="text-[11px] text-slate-500">
              Targeting limited to this channel's coverage area and neighboring states.
            </p>
          )}
        </>
      )}
      {showAddressSuggestion && (
        <button
          type="button"
          onClick={() => onChange([...selectedStates, addressSuggestedStateCode!])}
          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-slate-50 text-slate-700 hover:bg-slate-100 rounded-full border border-slate-200"
        >
          <Plus className="w-3 h-3" />
          Based on your address: {addressSuggestedStateCode} —{' '}
          {US_STATE_MAP[addressSuggestedStateCode!] ?? addressSuggestedStateCode}
        </button>
      )}

      {onboardingSuggestions.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">
            Suggested states
          </div>
          <div className="flex flex-wrap gap-1.5">
            {onboardingSuggestions.map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => onChange([...selectedStates, code])}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-slate-50 text-slate-700 hover:bg-slate-100 rounded-full border border-slate-200"
              >
                <Plus className="w-3 h-3" />
                {code} — {US_STATE_MAP[code]}
              </button>
            ))}
          </div>
        </div>
      )}
      {selectedStates.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] uppercase tracking-wide text-slate-500">
              {selectedStates.length} selected
            </span>
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-[12px] text-slate-500 hover:text-red-600 underline"
            >
              Clear all
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {selectedStates.map((code) => (
              <span
                key={code}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-slate-100 text-slate-800 rounded-full"
              >
                {code} — {US_STATE_MAP[code] ?? code}
                <button
                  type="button"
                  onClick={() => remove(code)}
                  aria-label={`Remove ${code}`}
                  className="text-slate-500 hover:text-red-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default StateSelector;
